import { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from "recharts";
import { ChevronLeft, ChevronRight, Clock, Droplet, Gauge, MapPin, RotateCcw, SlidersHorizontal, Truck } from "lucide-react";
import { ChartContainer } from "@/components/charts/ChartContainer";
import { Button } from "@/components/ui/Button";
import { DateInput } from "@/components/ui/DateInput";
import { Loader } from "@/components/ui/Loader";
import { currentLocalDateInput } from "@/utils/dateInput";
import type { ContratoDto, LocacaoDto, MaquinarioDto, OperationalDeltasDto, OperationalMetricsDto, PeriodRange, RegistroHorimetroDto } from "@/services/locacoes";
import { isEquipamentoAtivo, MAQUINARIO_STATUS_LABELS } from "@/services/locacoes";
import { formatNumber } from "@/utils/format";
import { cn } from "@/utils/cn";
import { HorasPeriodoModal } from "./HorasPeriodoModal";
import { LocacoesStatCard } from "./LocacoesStatCard";
import { ResumoKpiDetalheModal, contratoClienteDaMaquina, type ResumoKpiKind } from "./ResumoKpiDetalheModal";
import { MAQUINARIO_STATUS_THEME } from "./statusTheme";
import { formatHoras, formatPeriodLabel, locacoesTableHeadClass, locacoesTableWrapClass, shiftPeriodByLength, weekPeriod } from "./locacoesUtils";

const WEEKDAY_SHORT = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"] as const;

export interface ResumoDashboardFilters {
  maquinaId: string;
  clienteId: string;
  contratoId: string;
}

export const EMPTY_RESUMO_FILTERS: ResumoDashboardFilters = {
  maquinaId: "",
  clienteId: "",
  contratoId: "",
};

type ResumoFilterId = "periodo" | "maquina" | "cliente" | "contrato";

const FILTER_STORAGE_KEY = "semog-locacoes-dashboard-filtros-v2";
const FILTER_CHOICES: Array<{ id: ResumoFilterId; label: string }> = [
  { id: "periodo", label: "Período" },
  { id: "maquina", label: "Máquina" },
  { id: "cliente", label: "Cliente" },
  { id: "contrato", label: "Contrato" },
];

const filterSelectClass =
  "h-10 w-full cursor-pointer rounded-lg border border-slate-200 bg-slate-50 px-2.5 text-xs text-slate-800 outline-none transition-colors focus:border-brand-600 focus:ring-2 focus:ring-brand-600 sm:text-sm";

function readVisibleFilters(): ResumoFilterId[] {
  try {
    const raw = localStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return FILTER_CHOICES.map((item) => item.id);
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return FILTER_CHOICES.map((item) => item.id);
    const allowed = new Set(FILTER_CHOICES.map((item) => item.id));
    return parsed.filter((id): id is ResumoFilterId => allowed.has(id as ResumoFilterId));
  } catch {
    return FILTER_CHOICES.map((item) => item.id);
  }
}

interface Props {
  period: PeriodRange;
  onPeriodChange: (period: PeriodRange) => void;
  metrics: OperationalMetricsDto;
  deltas: OperationalDeltasDto;
  maquinarios: MaquinarioDto[];
  locacoes: LocacaoDto[];
  contratos: ContratoDto[];
  registrosHorimetro: RegistroHorimetroDto[];
  filters: ResumoDashboardFilters;
  onFiltersChange: (filters: ResumoDashboardFilters) => void;
  /** Dados do snapshot ainda não correspondem ao período e aos filtros pedidos. */
  ready?: boolean;
  loading?: boolean;
}

function weekdayTick(isoDate: string): string {
  const date = new Date(`${isoDate}T12:00:00`);
  const day = WEEKDAY_SHORT[date.getDay()] ?? "";
  const [, , dd] = isoDate.split("-");
  return `${day} ${dd}`;
}

function locacaoAtivaDaMaquina(locacoes: LocacaoDto[], maquinaId: string): LocacaoDto | undefined {
  return locacoes
    .filter((l) => l.status === "ativa" && l.maquinaId === maquinaId)
    .sort((a, b) => b.dataInicio.localeCompare(a.dataInicio))[0];
}

function obraClienteLabel(locacao: LocacaoDto | undefined, contratos: ContratoDto[]): string {
  if (!locacao) return "—";
  const contrato = locacao.contratoId ? contratos.find((c) => c.id === locacao.contratoId) : undefined;
  const cliente = contrato?.clienteSiengeNome?.trim();
  const obra = (locacao.obraNome || locacao.costCenterLabel || "").trim();
  const parts = [cliente, obra].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(" · ") : "Em locação";
}

export function LocacoesResumoDashboard({
  period,
  onPeriodChange,
  metrics,
  deltas,
  maquinarios,
  locacoes,
  contratos,
  registrosHorimetro,
  filters,
  onFiltersChange,
  ready = true,
  loading,
}: Props) {
  const [visibleFilters, setVisibleFilters] = useState<ResumoFilterId[]>(readVisibleFilters);
  const [customizing, setCustomizing] = useState(false);
  const [horasModalOpen, setHorasModalOpen] = useState(false);
  const [kpiModal, setKpiModal] = useState<ResumoKpiKind | null>(null);

  useEffect(() => {
    setHorasModalOpen(false);
    setKpiModal(null);
  }, [filters.clienteId, filters.contratoId, filters.maquinaId, period.endDate, period.startDate]);

  const showFilter = (id: ResumoFilterId) => visibleFilters.includes(id);

  const setVisible = (next: ResumoFilterId[]) => {
    setVisibleFilters(next);
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(next));
    const cleared: ResumoDashboardFilters = { ...filters };
    if (!next.includes("maquina")) cleared.maquinaId = "";
    if (!next.includes("cliente")) cleared.clienteId = "";
    if (!next.includes("contrato")) cleared.contratoId = "";
    if (
      cleared.maquinaId !== filters.maquinaId ||
      cleared.clienteId !== filters.clienteId ||
      cleared.contratoId !== filters.contratoId
    ) {
      onFiltersChange(cleared);
    }
  };

  const clientes = useMemo(() => {
    const map = new Map<string, string>();
    for (const contrato of contratos) {
      if (!contrato.clienteSiengeId || map.has(contrato.clienteSiengeId)) continue;
      map.set(contrato.clienteSiengeId, contrato.clienteSiengeNome || contrato.clienteSiengeId);
    }
    return [...map.entries()]
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
  }, [contratos]);

  const contratosOpcoes = useMemo(() => {
    const list = filters.clienteId
      ? contratos.filter((c) => c.clienteSiengeId === filters.clienteId)
      : contratos;
    return [...list].sort((a, b) => {
      const ano = (b.codigoAno ?? 0) - (a.codigoAno ?? 0);
      if (ano !== 0) return ano;
      return (b.codigoSequencial ?? 0) - (a.codigoSequencial ?? 0);
    });
  }, [contratos, filters.clienteId]);

  const maquinasOpcoes = useMemo(() => {
    return maquinarios
      .filter((m) => !isEquipamentoAtivo(m))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  }, [maquinarios]);

  const maquinaSelecionada = filters.maquinaId
    ? maquinarios.find((m) => m.id === filters.maquinaId)
    : undefined;

  const semanaAtual = weekPeriod(0);
  const periodoCustom =
    period.startDate !== semanaAtual.startDate || period.endDate !== semanaAtual.endDate;
  const hasFilters = Boolean(filters.maquinaId || filters.clienteId || filters.contratoId || periodoCustom);
  const hoje = currentLocalDateInput();

  const updateFilters = (patch: Partial<ResumoDashboardFilters>) => {
    const next: ResumoDashboardFilters = { ...filters, ...patch };
    if (patch.clienteId !== undefined && patch.clienteId !== filters.clienteId) {
      if (next.contratoId) {
        const contrato = contratos.find((c) => c.id === next.contratoId);
        if (!contrato || (next.clienteId && contrato.clienteSiengeId !== next.clienteId)) {
          next.contratoId = "";
        }
      }
      if (next.maquinaId && next.clienteId) {
        const daCliente = contratos.some(
          (c) => c.clienteSiengeId === next.clienteId && c.maquinaIds.includes(next.maquinaId),
        );
        if (!daCliente) next.maquinaId = "";
      }
    }
    if (patch.contratoId !== undefined && patch.contratoId !== filters.contratoId) {
      const contrato = contratos.find((c) => c.id === next.contratoId);
      if (contrato) next.clienteId = contrato.clienteSiengeId || next.clienteId;
      if (next.maquinaId && contrato && !contrato.maquinaIds.includes(next.maquinaId)) {
        next.maquinaId = "";
      }
    }
    if (patch.maquinaId !== undefined && next.maquinaId) {
      if (next.contratoId) {
        const contrato = contratos.find((c) => c.id === next.contratoId);
        if (!contrato?.maquinaIds.includes(next.maquinaId)) next.contratoId = "";
      }
      if (next.clienteId) {
        const daCliente = contratos.some(
          (c) => c.clienteSiengeId === next.clienteId && c.maquinaIds.includes(next.maquinaId),
        );
        if (!daCliente) next.clienteId = "";
      }
    }
    onFiltersChange(next);
  };

  const rows = useMemo(() => {
    const byId = new Map(metrics.consumoPorMaquina.map((row) => [row.maquinaId, row]));
    let list = maquinarios.filter((m) => !isEquipamentoAtivo(m));
    if (filters.maquinaId) {
      list = list.filter((m) => m.id === filters.maquinaId);
    } else if (filters.contratoId) {
      const ids = new Set(contratos.find((c) => c.id === filters.contratoId)?.maquinaIds ?? []);
      list = list.filter((m) => ids.has(m.id));
    } else if (filters.clienteId) {
      const ids = new Set(
        contratos.filter((c) => c.clienteSiengeId === filters.clienteId).flatMap((c) => c.maquinaIds),
      );
      list = list.filter((m) => ids.has(m.id));
    }
    return list
      .map((m) => {
        const consumo = byId.get(m.id);
        const horas = consumo?.horas ?? 0;
        const litros = consumo?.litros ?? 0;
        const litrosPorHora = consumo?.litrosPorHora ?? 0;
        const limite = consumo?.consumoLimiteLph ?? m.consumoLimiteLph ?? 0;
        const locacao = locacaoAtivaDaMaquina(locacoes, m.id);
        return {
          id: m.id,
          nome: m.nome,
          tipo: m.tipo || m.modelo || "—",
          status: m.status,
          horas,
          litros,
          litrosPorHora,
          limite,
          acimaDoLimite: limite > 0 && litrosPorHora > limite,
          local: obraClienteLabel(locacao, contratos),
        };
      })
      .sort((a, b) => b.horas - a.horas || a.nome.localeCompare(b.nome, "pt-BR"));
  }, [contratos, filters.clienteId, filters.contratoId, filters.maquinaId, locacoes, maquinarios, metrics.consumoPorMaquina]);

  const kpiLinhas = useMemo(() => {
    const toLinha = (row: (typeof rows)[number]) => {
      const vinculo = contratoClienteDaMaquina(row.id, locacoes, contratos);
      const consumo = metrics.consumoPorContrato.find((item) => {
        const contrato = contratos.find((c) => c.id === item.contratoId);
        return Boolean(contrato?.maquinaIds.includes(row.id));
      });
      const contrato = vinculo.contrato !== "Sem contrato"
        ? vinculo.contrato
        : consumo?.codigoContrato || "Sem contrato";
      const cliente = vinculo.cliente !== "Sem cliente"
        ? vinculo.cliente
        : consumo?.clienteSiengeNome || "Sem cliente";
      return {
        id: row.id,
        maquina: row.nome,
        cliente,
        contrato,
        obra: vinculo.obra === "—" ? row.local : vinculo.obra,
        horas: row.horas,
        litros: row.litros,
        litrosPorHora: row.litrosPorHora,
      };
    };
    const trabalharamRows = rows.filter((row) => row.horas > 0);
    const locadasSemHoraRows = rows.filter((row) => row.status === "locado" && row.horas <= 0);
    return {
      maquinas: trabalharamRows.map(toLinha),
      semHora: locadasSemHoraRows.map(toLinha),
      litros: rows.filter((row) => row.litros > 0).map(toLinha).sort((a, b) => b.litros - a.litros),
      consumo: rows
        .filter((row) => row.horas > 0 || row.litros > 0)
        .map(toLinha)
        .sort((a, b) => b.litrosPorHora - a.litrosPorHora),
    };
  }, [contratos, locacoes, metrics.consumoPorContrato, rows]);
  const trabalharam = kpiLinhas.maquinas.length;
  const locadasSemHora = kpiLinhas.semHora.length;

  const chartData = metrics.horasPorDia.map((point) => ({
    dia: weekdayTick(point.date),
    horas: point.horas,
  }));

  const consumoCard = maquinaSelecionada
    ? (rows[0]?.litrosPorHora ?? 0)
    : metrics.consumoMedioGeral;

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-card">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <SlidersHorizontal className="h-3.5 w-3.5 flex-shrink-0 text-brand-600" />
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Filtros</span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setCustomizing((open) => !open)}
          >
            Personalizar
          </Button>
        </div>

        {customizing ? (
          <div className="mb-3 flex flex-wrap gap-2">
            {FILTER_CHOICES.map((choice) => {
              const active = showFilter(choice.id);
              return (
                <button
                  key={choice.id}
                  type="button"
                  onClick={() =>
                    setVisible(
                      active
                        ? visibleFilters.filter((id) => id !== choice.id)
                        : [...visibleFilters, choice.id],
                    )
                  }
                  className={
                    active
                      ? "rounded-full border border-brand-600 bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-800"
                      : "rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600"
                  }
                  aria-pressed={active}
                >
                  {choice.label}
                </button>
              );
            })}
          </div>
        ) : null}

        <div className="flex min-w-0 flex-wrap items-end gap-2 sm:gap-3">
          {showFilter("periodo") ? (
            <>
              <div className="w-[132px] flex-shrink-0">
                <DateInput
                  label="Data inicial"
                  value={period.startDate}
                  max={period.endDate || hoje}
                  disabled={loading}
                  onChange={(startDate) =>
                    onPeriodChange({
                      startDate,
                      endDate: startDate > period.endDate ? startDate : period.endDate,
                    })
                  }
                />
              </div>
              <div className="w-[132px] flex-shrink-0">
                <DateInput
                  label="Data final"
                  value={period.endDate}
                  min={period.startDate || undefined}
                  max={hoje}
                  disabled={loading}
                  onChange={(endDate) =>
                    onPeriodChange({
                      startDate: endDate < period.startDate ? endDate : period.startDate,
                      endDate,
                    })
                  }
                />
              </div>
            </>
          ) : null}
          {showFilter("maquina") ? (
            <label className="min-w-[180px] max-w-[260px] flex-1">
              <span className="mb-1.5 block truncate text-[10px] font-semibold uppercase tracking-wide text-slate-500 sm:text-xs">
                Máquina
              </span>
              <select
                value={filters.maquinaId}
                onChange={(e) => updateFilters({ maquinaId: e.target.value })}
                className={filterSelectClass}
                disabled={loading || maquinasOpcoes.length === 0}
              >
                <option value="">Todas as máquinas</option>
                {maquinasOpcoes.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.codigoInterno ? `${m.nome} · ${m.codigoInterno}` : m.nome}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {showFilter("cliente") ? (
            <label className="min-w-[160px] max-w-[240px] flex-1">
              <span className="mb-1.5 block truncate text-[10px] font-semibold uppercase tracking-wide text-slate-500 sm:text-xs">
                Cliente
              </span>
              <select
                value={filters.clienteId}
                onChange={(e) => updateFilters({ clienteId: e.target.value })}
                className={filterSelectClass}
                disabled={loading}
              >
                <option value="">Todos os clientes</option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {showFilter("contrato") ? (
            <label className="min-w-[160px] max-w-[240px] flex-1">
              <span className="mb-1.5 block truncate text-[10px] font-semibold uppercase tracking-wide text-slate-500 sm:text-xs">
                Contrato
              </span>
              <select
                value={filters.contratoId}
                onChange={(e) => updateFilters({ contratoId: e.target.value })}
                className={filterSelectClass}
                disabled={loading}
              >
                <option value="">Todos os contratos</option>
                {contratosOpcoes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {filters.clienteId ? c.codigoContrato : `${c.codigoContrato} — ${c.clienteSiengeNome}`}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {hasFilters ? (
            <Button
              type="button"
              variant="ghost"
              size="md"
              onClick={() => {
                onFiltersChange(EMPTY_RESUMO_FILTERS);
                onPeriodChange(weekPeriod(0));
              }}
              disabled={loading}
              leftIcon={<RotateCcw className="h-4 w-4" />}
            >
              Limpar
            </Button>
          ) : null}
        </div>
        {maquinaSelecionada ? (
          <p className="mt-3 text-sm text-slate-600">
            Exibindo somente <strong className="font-semibold text-slate-800">{maquinaSelecionada.nome}</strong>
            : horas, abastecimento e consumo desta máquina no período.
          </p>
        ) : null}
      </div>

      {!ready ? (
        <div className="flex justify-center py-16">
          <Loader size="lg" label="Carregando resumo da semana..." />
        </div>
      ) : (
      <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-slate-800">
            {maquinaSelecionada ? maquinaSelecionada.nome : "Resumo do período"}
          </h2>
          <p className="mt-0.5 text-sm text-slate-500">
            {formatPeriodLabel(period)} · comparado ao período anterior
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => onPeriodChange(shiftPeriodByLength(period, -1))}
            leftIcon={<ChevronLeft className="h-4 w-4" />}
            disabled={loading}
          >
            Período anterior
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => onPeriodChange(shiftPeriodByLength(period, 1))}
            disabled={period.endDate >= hoje || loading}
            leftIcon={<ChevronRight className="h-4 w-4" />}
          >
            Próximo
          </Button>
          {periodoCustom ? (
            <Button type="button" variant="ghost" size="sm" onClick={() => onPeriodChange(weekPeriod(0))} disabled={loading}>
              Semana atual
            </Button>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-5">
        <LocacoesStatCard
          title="Horas do período"
          value={formatHoras(metrics.horasTrabalhadas)}
          subtitle="ver contratos e clientes"
          variation={deltas.horasTrabalhadas}
          icon={<Clock className="h-5 w-5" />}
          accentClass="border-l-4 border-l-emerald-400"
          loading={loading}
          onClick={() => setHorasModalOpen(true)}
        />
        <LocacoesStatCard
          title="Máquinas que trabalharam"
          value={trabalharam}
          subtitle="ver contratos e clientes"
          icon={<Truck className="h-5 w-5" />}
          accentClass="border-l-4 border-l-sky-400"
          loading={loading}
          onClick={() => setKpiModal("maquinas")}
        />
        <LocacoesStatCard
          title="Locadas sem hora"
          value={locadasSemHora}
          subtitle="ver contratos e clientes"
          icon={<MapPin className="h-5 w-5" />}
          accentClass="border-l-4 border-l-amber-400"
          loading={loading}
          onClick={() => setKpiModal("semHora")}
        />
        <LocacoesStatCard
          title="Litros abastecidos"
          value={`${formatNumber(metrics.litrosAbastecidos, 0)} L`}
          subtitle="ver contratos e clientes"
          variation={deltas.litrosAbastecidos}
          icon={<Droplet className="h-5 w-5" />}
          accentClass="border-l-4 border-l-blue-400"
          loading={loading}
          onClick={() => setKpiModal("litros")}
        />
        <LocacoesStatCard
          title="Consumo médio"
          value={`${formatNumber(consumoCard, 1)} L/h`}
          subtitle="ver contratos e clientes"
          variation={maquinaSelecionada ? undefined : deltas.consumoMedio}
          icon={<Gauge className="h-5 w-5" />}
          accentClass="border-l-4 border-l-purple-400"
          loading={loading}
          onClick={() => setKpiModal("consumo")}
        />
      </div>

      <ChartContainer
        title="Horas por dia"
        subtitle="Horas trabalhadas em cada dia do período"
        height={240}
        loading={loading}
        empty={chartData.every((point) => point.horas <= 0)}
        emptyMessage={
          maquinaSelecionada
            ? `Nenhuma hora apontada para ${maquinaSelecionada.nome} neste período.`
            : "Nenhuma hora apontada neste período."
        }
      >
        <BarChart data={chartData} margin={{ top: 12, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
          <XAxis dataKey="dia" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={32} />
          <Tooltip
            formatter={(value: unknown) => [formatHoras(Number(value)), "Horas"]}
            contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}
          />
          <Bar dataKey="horas" fill="#22c55e" radius={[4, 4, 0, 0]} maxBarSize={40} />
        </BarChart>
      </ChartContainer>

      <div className={locacoesTableWrapClass}>
        <div className="border-b border-slate-100 px-5 py-4">
          <h3 className="text-sm font-semibold text-slate-800">
            {maquinaSelecionada ? "Máquina no período" : "Frota no período"}
          </h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Horas, abastecimento e obra da locação ativa. L/h em destaque quando passa do limite da máquina.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className={locacoesTableHeadClass}>
              <tr>
                <th className="px-4 py-3">Máquina</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Obra / cliente</th>
                <th className="px-4 py-3 text-right">Horas</th>
                <th className="px-4 py-3 text-right">Litros</th>
                <th className="px-4 py-3 text-right">L/h</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500">
                    {hasFilters ? "Nenhuma máquina corresponde aos filtros." : "Nenhuma máquina cadastrada."}
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const theme = MAQUINARIO_STATUS_THEME[row.status];
                  return (
                    <tr key={row.id} className={cn(row.acimaDoLimite && "bg-red-50/70")}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-800">{row.nome}</p>
                        <p className="text-xs text-slate-500">{row.tipo}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold",
                            theme.badge,
                          )}
                        >
                          <span className={cn("h-1 w-1 rounded-full", theme.dot)} />
                          {MAQUINARIO_STATUS_LABELS[row.status]}
                        </span>
                      </td>
                      <td className="max-w-[18rem] px-4 py-3 text-slate-600">
                        <span className="line-clamp-2">{row.local}</span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium tabular-nums text-slate-800">
                        {formatHoras(row.horas)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                        {formatNumber(row.litros, 0)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        <span className={cn("font-medium", row.acimaDoLimite ? "text-red-700" : "text-slate-700")}>
                          {row.horas > 0 || row.litros > 0 ? formatNumber(row.litrosPorHora, 1) : "—"}
                        </span>
                        {row.acimaDoLimite ? (
                          <p className="text-[10px] font-semibold text-red-600">acima de {formatNumber(row.limite, 1)}</p>
                        ) : null}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      <HorasPeriodoModal
        open={horasModalOpen}
        onClose={() => setHorasModalOpen(false)}
        period={period}
        totalHoras={metrics.horasTrabalhadas}
        registros={registrosHorimetro}
        contratos={contratos}
        locacoes={locacoes}
        maquinarios={maquinarios}
        maquinaId={filters.maquinaId || undefined}
      />
      {kpiModal ? (
        <ResumoKpiDetalheModal
          open
          onClose={() => setKpiModal(null)}
          kind={kpiModal}
          period={period}
          value={
            kpiModal === "maquinas"
              ? String(trabalharam)
              : kpiModal === "semHora"
                ? String(locadasSemHora)
                : kpiModal === "litros"
                  ? `${formatNumber(metrics.litrosAbastecidos, 0)} L`
                  : `${formatNumber(consumoCard, 1)} L/h`
          }
          maquinaNome={maquinaSelecionada?.nome}
          linhas={kpiLinhas[kpiModal]}
        />
      ) : null}
      </>
      )}
    </div>
  );
}
